import { motion } from "framer-motion";
import {
  FaLinkedinIn,
  FaTwitter,
  FaInstagram,
  FaGlobe,
  FaFacebook,
  FaYoutube,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { Link, useNavigate } from "react-router-dom";
import React from "react";

export default function Footer() {
  const navigate = useNavigate();

  // Scroll to top helper
  const handleScrollToTop = (e, to, isNavLink = false) => {
    // For in-page anchors, might skip navigation and just scroll
    if (to && to.startsWith("/#")) {
      const id = to.split("#")[1];
      if (id) {
        e.preventDefault();
        // Defer scroll to after dom settles
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) {
            el.scrollIntoView({ behavior: "smooth" });
          } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }, 0);
        // Trigger route navigation (optional, if anchor keeps route as /)
        if (window.location.pathname !== "/") {
          navigate("/");
        }
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      if (isNavLink) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
    // For regular links (anchors etc.), allow normal navigation.
  };

  // Now quickLinks has both label and link
  const quickLinks = [
    { label: "Home", link: "/" },
    { label: "About Us", link: "/about" },
    // { label: "Advisory Councils", link: "/#advisory-councils" },
    { label: "Services", link: "/services" },
    
    { label: "Events", link: "/news-events" },
    { label: "Contact Us", link: "/contact" },
    { label: "Get A Quote", link: "/contact" },
  ];

  const services = [
    "Global Business Matchmaking",
    "Market Intelligence & Economic Research",
    "Strategic Expansion & Delegation Support",
    "Digital Transformation & Innovation Support",
    "Policy Advocacy & Thought Leadership",
  ];

  // Contact locations (for rendering updated country, address, phone)
  const officeLocations = [
    {
      country: "United Kingdom",
      address: "First Floor 30 High Road, Wood Green, London N226BX",
      phone: "+44 7405649308",
    },
    {
      country: "Hungary",
      address: "1114 Budapest, Mészöly utca 4 Al. 1. ép.",
      phone: "+36 707201234",
    },
    {
      country: "Dubai",
      address:
        "17th Floor Fahidi Heights, (Near Four Points Sheraton Hotel) Bur Dubai, Dubai. UAE.",
      phone: "+971 555067758",
    },
    {
      country: "India",
      // address: "Unitech Cyber Park, Sector-39, Gurugram",
      phone: "+91 9892416044",
    },
  ];

  // Bottom policy handlers
  const handleBottomLinkClick = (e, href) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
    // If you ever implement actual pages, add navigation here.
  };

  return (
    <footer className="bg-gray-900 text-gray-300 pt-24 pb-10 px-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className=" mx-auto flex flex-col lg:flex-row justify-between gap-16"
      >
        {/* Column 1 - About Indo European Business Council */}
        <div className="flex-1 space-y-6">
          <div className="p-2 bg-white rounded-md w-fit">
            <img src="/logo.avif" className="h-20 " alt="IEBC Logo" />
          </div>

          <h3 className="text-3xl font-Agatho_Regular  font-semibold text-white">
            Indo European Business Council
          </h3>
          <p className="text-gray-400 leading-relaxed text-base">
            Fostering Unity Through Global Partnerships — The Indo European
            Business Council builds bridges across cultural, commercial, and
            institutional landscapes between India and Europe.
          </p>
          <div className="flex items-center gap-4 pt-2">
            {[
              {
                Icon: FaFacebook,
                href: "https://www.facebook.com/IndoEUBusinessCouncil",
                label: "Facebook",
              },
              {
                Icon: FaXTwitter,
                href: "https://x.com/IEBC123",
                label: "Twitter",
              },
              {
                Icon: FaInstagram,
                href: "https://www.instagram.com/indoeuropeanbusinesscouncil/",
                label: "Instagram",
              },
              {
                Icon: FaYoutube,
                href: "https://www.youtube.com/@indoeuropeancouncil",
                label: "YouTube",
              },
              {
                Icon: FaLinkedinIn,
                href: "https://www.linkedin.com/company/indoeuropeanbusinesscouncil/?originalSubdomain=uk",
                label: "LinkedIn",
              },
            ].map(({ Icon, href, label }, i) => (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-700 hover:bg-blue-700 hover:text-white transition"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>

        {/* Column 2 - Quick Links */}
        <div className="flex-1">
          <h4 className="text-2xl font-semibold font-Agatho_Regular text-white mb-6">
            Quick Links
          </h4>
          <ul className="space-y-3 text-gray-400">
            {quickLinks.map((item, i) => (
              <li key={i}>
                {item.link.startsWith("/") ? (
                  item.link.includes("#") ? (
                    // Link with #anchor (e.g., /#id)
                    <a
                      href={item.link}
                      className="hover:text-blue-500 transition-colors duration-200"
                      onClick={e => handleScrollToTop(e, item.link, true)}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.link}
                      className="hover:text-blue-500 transition-colors duration-200"
                      onClick={e => handleScrollToTop(e, item.link, true)}
                    >
                      {item.label}
                    </Link>
                  )
                ) : (
                  <a
                    href={item.link}
                    className="hover:text-blue-500 transition-colors duration-200"
                    onClick={e => handleScrollToTop(e, item.link)}
                  >
                    {item.label}
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Column 3 - Services */}
        <div className="flex-1">
          <h4 className="text-2xl font-Agatho_Regular  font-semibold text-white   mb-6">
            Services
          </h4>
          <ul className="space-y-3 text-gray-400">
            {services.map((service, i) => (
              <li key={i}>
                <a
                  href="/services"
                  className="hover:text-blue-500 transition-colors duration-200"
                  onClick={e => { 
                    // Always scroll to top on service click
                    window.scrollTo({ top: 0, behavior: "smooth" }); 
                  }}
                >
                  {service}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 4 - Contact Info */}
        <div className="flex-2 space-y-6">
          <h4 className="text-2xl font-Agatho_Regular font-semibold text-white mb-4">
            Contact
          </h4>
          <div className="space-y-3">
            {officeLocations.map((loc, i) => (
              <div key={i} className="mb-2">
                <p className="text-gray-400 font-medium text-base">
                  {loc.country}
                </p>
                <p className="text-gray-400 whitespace-pre-line text-sm mb-1">
                  {loc.address}
                </p>
                <p className="text-gray-400 text-sm mb-1">
                  <span className="font-medium text-gray-300">Phone: </span>
                  <a
                    className="hover:underline text-blue-400"
                    href={`tel:${loc.phone.replace(/\s/g, "")}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  >
                    {loc.phone}
                  </a>
                </p>
              </div>
            ))}
          </div>
          <p className="text-gray-400">
            <span className="block font-medium text-gray-300">Email:</span>
            <a
              href="mailto:iebc@indoeuropeancouncil.eu"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="hover:text-blue-400 transition-colors duration-200"
            >
              iebc@indoeuropeancouncil.eu
            </a>
          </p>
          {/* <p className="text-gray-400">
            <span className="block font-medium text-gray-300">
              Business Hours:
            </span>
            9:10 AM – 5:30 PM (Mon–Fri)
          </p> */}
        </div>
      </motion.div>

      {/* Bottom Bar */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="max-w-7xl mx-auto border-t border-gray-800 mt-16 pt-8 flex flex-col sm:flex-row items-center justify-between text-sm text-gray-500"
      >
        <p>
          © {new Date().getFullYear()} Indo European Business Council. All
          rights reserved.
        </p>
        <div className="flex gap-6 mt-4 sm:mt-0">
          <a
            href="#"
            className="hover:text-gray-300 transition"
            onClick={e => handleBottomLinkClick(e, "#")}
          >
            Privacy Policy
          </a>
          <a
            href="#"
            className="hover:text-gray-300 transition"
            onClick={e => handleBottomLinkClick(e, "#")}
          >
            Terms of Service
          </a>
        </div>
      </motion.div>
    </footer>
  );
}
